import { useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import {
  PixelConfigResponse,
  CreatePixelConfigRequest,
  UpdatePixelConfigRequest,
  PixelProvider,
  PixelMode,
  MetaEvent,
  GoogleEvent,
  TikTokEvent,
} from "@codway-dev/shared";
import {
  SectionCard,
  CollapsibleSectionCard,
} from "../../components/shared/EditorShared";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { usePixelTest } from "../../hooks/usePixelTest";

// ── Option catalogs (labels in Spanish; values match the contract enums) ──────

const PROVIDER_OPTIONS = [
  { value: "META", label: "Meta (Facebook / Instagram)" },
  { value: "GOOGLE", label: "Google" },
  { value: "TIKTOK", label: "TikTok" },
] as const;

const MODE_OPTIONS = [
  { value: "CLIENT_ONLY", label: "Solo cliente" },
  { value: "SERVER_ONLY", label: "Solo servidor" },
  { value: "BOTH", label: "Cliente y servidor" },
] as const;

const EVENT_OPTIONS: Record<PixelProvider, { value: string; label: string }[]> =
  {
    META: [
      {
        value: "InitiateCheckout",
        label: "InitiateCheckout (inicio de checkout)",
      },
      { value: "Purchase", label: "Purchase (compra)" },
      { value: "AddToCart", label: "AddToCart (añadir al carrito)" },
      { value: "AddPaymentInfo", label: "AddPaymentInfo (datos de pago)" },
    ],
    GOOGLE: [
      { value: "begin_checkout", label: "begin_checkout" },
      { value: "purchase", label: "purchase" },
      { value: "add_to_cart", label: "add_to_cart" },
    ],
    TIKTOK: [
      { value: "InitiateCheckout", label: "InitiateCheckout" },
      { value: "Purchase", label: "Purchase" },
      { value: "AddToCart", label: "AddToCart" },
    ],
  };

const CONTENT_ID_FORMAT_OPTIONS = [
  {
    value: "shopify_{REGION}_{ProductId}",
    label: "shopify_{REGION}_{ProductId}",
  },
  {
    value: "shopify_{REGION}_{VariantId}",
    label: "shopify_{REGION}_{VariantId}",
  },
  { value: "{ProductId}", label: "{ProductId}" },
  { value: "{VariantId}", label: "{VariantId}" },
] as const;

type ContentIdFormat = (typeof CONTENT_ID_FORMAT_OPTIONS)[number]["value"];

const DEFAULT_CONTENT_ID_FORMAT: ContentIdFormat =
  "shopify_{REGION}_{ProductId}";

// ── Initial form values from an existing pixel (edit) ─────────────────────────

interface InitialFormOptions {
  content_id_format: ContentIdFormat;
  test_event_code: string;
}

function readInitialOptions(initial?: PixelConfigResponse): InitialFormOptions {
  const defaults: InitialFormOptions = {
    content_id_format: DEFAULT_CONTENT_ID_FORMAT,
    test_event_code: "",
  };
  if (initial?.provider === "META") {
    return {
      content_id_format: initial.options.content_id_format,
      test_event_code: initial.options.test_event_code ?? "",
    };
  }
  if (initial?.provider === "TIKTOK") {
    return {
      ...defaults,
      test_event_code: initial.options.test_event_code ?? "",
    };
  }
  return defaults;
}

function credentialHint(initial?: PixelConfigResponse): string | undefined {
  if (!initial) return undefined;
  if (initial.provider === "META" || initial.provider === "TIKTOK") {
    return initial.access_token_hint;
  }
  return initial.options.api_secret_hint;
}

interface PixelFormProps {
  /** Present → edit mode; absent → create mode. */
  initial?: PixelConfigResponse;
  onSave: (body: CreatePixelConfigRequest) => Promise<void>;
  /** Soft delete (is_active=false) — an update, so it takes the update type. */
  onDelete?: (body: UpdatePixelConfigRequest) => Promise<void>;
  onCancel: () => void;
}

export default function PixelForm({
  initial,
  onSave,
  onDelete,
  onCancel,
}: PixelFormProps) {
  const isEdit = Boolean(initial);
  const initialOpts = readInitialOptions(initial);
  const hint = credentialHint(initial);

  const [provider, setProvider] = useState<PixelProvider>(
    initial?.provider ?? "META"
  );
  const [label, setLabel] = useState(initial?.label ?? "");
  const [pixelId, setPixelId] = useState(initial?.pixel_id ?? "");
  const [mode, setMode] = useState<PixelMode>(initial?.mode ?? "CLIENT_ONLY");
  const [accessToken, setAccessToken] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [events, setEvents] = useState<string[]>(initial?.events_enabled ?? []);
  const [contentIdFormat, setContentIdFormat] = useState<ContentIdFormat>(
    initialOpts.content_id_format
  );
  const [testEventCode, setTestEventCode] = useState(
    initialOpts.test_event_code
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const requiresServer = mode === "SERVER_ONLY" || mode === "BOTH";
  const usesAccessToken = provider === "META" || provider === "TIKTOK";

  const { testing, runTest } = usePixelTest();
  // "Probar" tests the SAVED pixel by id, so it only shows in edit mode and its
  // behaviour follows the stored mode (not unsaved form edits). The client-side
  // leg fires from admin.shopify.com, so its allow-list warning is shown only
  // when the saved mode actually fires client-side.
  const testFiresClient =
    isEdit && (initial!.mode === "CLIENT_ONLY" || initial!.mode === "BOTH");

  // Switching provider resets the provider-specific selections.
  function handleProviderChange(next: PixelProvider) {
    setProvider(next);
    setEvents([]);
    setAccessToken("");
    setApiSecret("");
    setTestEventCode("");
    setContentIdFormat(DEFAULT_CONTENT_ID_FORMAT);
  }

  function toggleEvent(value: string, checked: boolean) {
    setEvents((prev) =>
      checked
        ? // Idempotent: the Polaris s-checkbox can emit onChange twice per
          // click, so guard against adding the same event more than once.
          prev.includes(value)
          ? prev
          : [...prev, value]
        : prev.filter((e) => e !== value)
    );
  }

  function buildRequest(active: boolean): CreatePixelConfigRequest {
    const base = {
      label: label.trim(),
      pixel_id: pixelId.trim(),
      mode,
      is_active: active,
    };
    // Dedup as a safety net so a row can never persist duplicate events.
    const uniqueEvents = Array.from(new Set(events));
    // send_add_to_cart / send_add_payment_info are derived from the event
    // selection rather than from their own controls: the widget double-gates
    // AddToCart on both events_enabled AND options.send_add_to_cart
    // (pixelTracking.isEnabled), so the event checkbox is the single source of
    // truth and these flags must mirror it to avoid a silent no-fire.
    const sendAddToCart = uniqueEvents.includes("AddToCart");
    const sendAddPaymentInfo = uniqueEvents.includes("AddPaymentInfo");
    const token = accessToken.trim()
      ? { access_token: accessToken.trim() }
      : {};
    const tec = testEventCode.trim()
      ? { test_event_code: testEventCode.trim() }
      : {};

    if (provider === "META") {
      return {
        ...base,
        provider: "META",
        events_enabled: uniqueEvents as MetaEvent[],
        options: {
          content_id_format: contentIdFormat,
          send_add_to_cart: sendAddToCart,
          send_add_payment_info: sendAddPaymentInfo,
          ...tec,
        },
        ...token,
      };
    }
    if (provider === "GOOGLE") {
      return {
        ...base,
        provider: "GOOGLE",
        events_enabled: uniqueEvents as GoogleEvent[],
        options: apiSecret.trim() ? { api_secret: apiSecret.trim() } : {},
      };
    }
    return {
      ...base,
      provider: "TIKTOK",
      events_enabled: uniqueEvents as TikTokEvent[],
      options: { send_add_to_cart: sendAddToCart, ...tec },
      ...token,
    };
  }

  // Soft delete just deactivates the saved pixel, so its payload is rebuilt from
  // `initial` (always valid) rather than the current form state — that way an
  // unsaved edit (e.g. switching into a server mode without a credential) can't
  // make the request invalid. Credentials are omitted (the API preserves the
  // stored ones); Google's api_secret_hint is dropped.
  function buildDeleteRequest(): UpdatePixelConfigRequest {
    const src = initial!;
    const base = {
      label: src.label,
      pixel_id: src.pixel_id,
      mode: src.mode,
      is_active: false,
    };
    if (src.provider === "META") {
      return {
        ...base,
        provider: "META",
        events_enabled: src.events_enabled,
        options: src.options,
      };
    }
    if (src.provider === "GOOGLE") {
      return {
        ...base,
        provider: "GOOGLE",
        events_enabled: src.events_enabled,
        options: {},
      };
    }
    return {
      ...base,
      provider: "TIKTOK",
      events_enabled: src.events_enabled,
      options: src.options,
    };
  }

  function validate(): string | null {
    if (!label.trim()) return "El nombre (label) es obligatorio.";
    if (!pixelId.trim()) return "El ID del píxel es obligatorio.";
    if (events.length === 0) return "Selecciona al menos un evento.";
    // Server modes need a credential, but on edit an existing one (shown as the
    // hint) is kept when the field is left empty — the API preserves it. So only
    // require input when there's nothing stored to preserve (create, or a pixel
    // being switched into a server mode for the first time).
    if (requiresServer && !hint) {
      if (usesAccessToken && !accessToken.trim()) {
        return "Ingresa el Access Token para el modo servidor.";
      }
      if (provider === "GOOGLE" && !apiSecret.trim()) {
        return "Ingresa el API Secret para el modo servidor.";
      }
    }
    return null;
  }

  // Save + Discard are driven by Shopify's save bar (app convention) rather than
  // inline buttons. canSave gates the Save button on client validation; the hook
  // shows the success/error toast and hides the bar. The parent's onSave closes
  // the form on success (which unmounts this component and dismisses the bar).
  useShopifySaveBar({
    isDirty: true,
    isSaving: saving,
    canSave: validate() === null,
    onSave: () => onSave(buildRequest(true)),
    onDiscard: onCancel,
  });

  // Delete skips validate() by design — soft-deleting a pixel must not be gated
  // on the form's required/credential fields (see buildDeleteRequest).
  async function runDelete() {
    // `saving` guards against rapid double-clicks before the disabled state
    // re-renders, which would otherwise fire two delete requests.
    if (!onDelete || saving) return;

    // Deleting a pixel stops production tracking, so confirm before proceeding.
    const confirmed = window.confirm(
      "¿Estás seguro de que deseas eliminar este píxel? Dejará de registrar conversiones y se perderá su seguimiento. Esta acción no se puede deshacer."
    );
    if (!confirmed) return;

    setError(null);
    setSaving(true);
    try {
      await onDelete(buildDeleteRequest());
      // On success the parent unmounts this form, so we deliberately don't reset
      // `saving` here — that would set state on an unmounted component. The
      // `saving` reset lives only in catch, where the form stays mounted.
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo eliminar el píxel."
      );
      setSaving(false);
    }
  }

  return (
    <SectionCard>
      <s-stack direction="block" gap="base">
        <s-heading>
          <strong>{isEdit ? "Editar píxel" : "Añadir píxel"}</strong>
        </s-heading>

        {error && (
          <s-banner tone="critical">
            <s-text>{error}</s-text>
          </s-banner>
        )}

        {/* Tipo */}
        <s-select
          label="Tipo"
          value={provider}
          disabled={isEdit}
          details={
            isEdit
              ? "El tipo no se puede cambiar después de crear el píxel."
              : undefined
          }
          onChange={(e: CallbackEvent<"s-select">) =>
            handleProviderChange(e.currentTarget.value as PixelProvider)
          }
        >
          {PROVIDER_OPTIONS.map((o) => (
            <s-option key={o.value} value={o.value}>
              {o.label}
            </s-option>
          ))}
        </s-select>

        {/* Label (required) */}
        <s-text-field
          label="Label"
          required
          placeholder="Ej: Píxel principal"
          value={label}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            setLabel(e.currentTarget.value)
          }
        />

        {/* Pixel ID */}
        <s-text-field
          label="Pixel ID"
          required
          placeholder="Identificador del píxel"
          value={pixelId}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            setPixelId(e.currentTarget.value)
          }
        />

        {/* Tracking mode */}
        <s-select
          label="Modo de seguimiento"
          value={mode}
          onChange={(e: CallbackEvent<"s-select">) =>
            setMode(e.currentTarget.value as PixelMode)
          }
        >
          {MODE_OPTIONS.map((o) => (
            <s-option key={o.value} value={o.value}>
              {o.label}
            </s-option>
          ))}
        </s-select>

        {/* Server credentials — only for SERVER_ONLY / BOTH. Masked
            (s-password-field) since these are secrets. */}
        {requiresServer && usesAccessToken && (
          <s-password-field
            label="Access Token"
            required={!hint}
            placeholder={
              hint ? `Actual: ${hint}` : "Token de acceso del servidor"
            }
            details={
              hint
                ? "Por seguridad no mostramos el token guardado. Déjalo vacío para mantener el actual o escribe uno nuevo para reemplazarlo."
                : undefined
            }
            value={accessToken}
            onInput={(e: CallbackEvent<"s-password-field">) =>
              setAccessToken(e.currentTarget.value)
            }
          />
        )}
        {requiresServer && provider === "GOOGLE" && (
          <s-password-field
            label="API Secret"
            required={!hint}
            placeholder={
              hint ? `Actual: ${hint}` : "API secret de Measurement Protocol"
            }
            details={
              hint
                ? "Por seguridad no mostramos el secreto guardado. Déjalo vacío para mantener el actual o escribe uno nuevo para reemplazarlo."
                : undefined
            }
            value={apiSecret}
            onInput={(e: CallbackEvent<"s-password-field">) =>
              setApiSecret(e.currentTarget.value)
            }
          />
        )}

        {/* Probar — fires a test event for the saved pixel. Server leg returns a
            real pass/fail; the client leg can't be acked, so we point the
            merchant to the provider's debug view. */}
        {isEdit && (
          <s-stack direction="block" gap="small-200">
            {testFiresClient && (
              <s-banner tone="info">
                <s-text>
                  Antes de probar: añade &quot;admin.shopify.com&quot; a la
                  lista de permitidos de los permisos de tráfico de tu píxel; de
                  lo contrario, el evento de prueba se bloquea y puede parecer
                  un error.
                </s-text>
              </s-banner>
            )}
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <s-button
                onClick={() => runTest(initial!)}
                loading={testing}
                disabled={testing}
              >
                Probar
              </s-button>
              <s-text color="subdued">
                Envía un evento de prueba para verificar la configuración.
              </s-text>
            </s-stack>
          </s-stack>
        )}

        {/* Events */}
        <s-stack direction="block" gap="small-200">
          <s-stack direction="inline" gap="small-100" alignItems="center">
            <s-text type="strong">Eventos a enviar</s-text>
            <span
              className="text-red-500 font-semibold"
              title="Campo obligatorio"
              aria-label="Obligatorio"
            >
              *
            </span>
            <s-text color="subdued">(Selecciona al menos 1 evento)</s-text>
          </s-stack>
          {EVENT_OPTIONS[provider].map((ev) => (
            <s-checkbox
              key={ev.value}
              label={ev.label}
              checked={events.includes(ev.value)}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                toggleEvent(ev.value, e.currentTarget.checked)
              }
            />
          ))}
        </s-stack>

        {/* Provider options (Meta / TikTok) — Google has none */}
        {(provider === "META" || provider === "TIKTOK") && (
          <CollapsibleSectionCard
            header={
              <s-text>
                <strong>Opciones avanzadas</strong>
              </s-text>
            }
            defaultOpen={false}
          >
            <s-stack direction="block" gap="base">
              {provider === "META" && (
                <s-select
                  label="Formato de content_id"
                  value={contentIdFormat}
                  onChange={(e: CallbackEvent<"s-select">) =>
                    setContentIdFormat(e.currentTarget.value as ContentIdFormat)
                  }
                >
                  {CONTENT_ID_FORMAT_OPTIONS.map((o) => (
                    <s-option key={o.value} value={o.value}>
                      {o.label}
                    </s-option>
                  ))}
                </s-select>
              )}

              <s-text-field
                label="Test event code (opcional)"
                placeholder="Código de evento de prueba"
                value={testEventCode}
                onInput={(e: CallbackEvent<"s-text-field">) =>
                  setTestEventCode(e.currentTarget.value)
                }
              />
            </s-stack>
          </CollapsibleSectionCard>
        )}

        {/* Delete stays in-form — the save bar only hosts Save + Discard. */}
        {isEdit && onDelete && (
          <s-stack direction="inline" justifyContent="end">
            <s-button
              variant="tertiary"
              tone="critical"
              icon="delete"
              loading={saving}
              onClick={runDelete}
            >
              Eliminar píxel
            </s-button>
          </s-stack>
        )}
      </s-stack>
    </SectionCard>
  );
}
